import * as Immutable from 'seamless-immutable';
import {
  ISSUE_ROOT_CAUSES_FETCH_API,
  ISSUE_ROOT_CAUSES_FETCH_API_FULFILLED,
  ISSUE_ROOT_CAUSES_FETCH_API_REJECTED,
  ISSUE_ROOT_CAUSE_CATEGORIES_CREATE_START,
  ISSUE_ROOT_CAUSE_CATEGORIES_CREATE_API_FULFILLED,
  ISSUE_ROOT_CAUSE_CATEGORIES_CREATE_API_REJECTED,
  ISSUE_ROOT_CAUSES_CREATE_START,
  ISSUE_ROOT_CAUSES_CREATE_API_FULFILLED,
  ISSUE_ROOT_CAUSES_CREATE_API_REJECTED,
  ISSUE_ROOT_CAUSES_UPDATE_TITLE_START,
  ISSUE_ROOT_CAUSES_UPDATE_TITLE_API_FULFILLED,
  ISSUE_ROOT_CAUSES_UPDATE_STATUS_START,
  ISSUE_ROOT_CAUSES_UPDATE_STATUS_API_FULFILLED,
  ISSUE_ROOT_CAUSES_DELETE_START,
  ISSUE_ROOT_CAUSES_DELETE_API_REJECTED,
  ISSUE_ROOT_CAUSES_DELETE_API_FULFILLED,
  ISSUE_ROOT_CAUSE_CATEGORIES_DELETE_START,
  ISSUE_ROOT_CAUSE_CATEGORIES_DELETE_API_REJECTED,
  ISSUE_ROOT_CAUSE_CATEGORIES_DELETE_API_FULFILLED,
  ISSUE_ROOT_CAUSES_TOGGLE_TITLE_EDITED_ROOT_CAUSE,
  ISSUE_ROOT_CAUSES_TOGGLE_ROOT_CAUSE_CATEGORY_IN_USE_MODAL,
  ISSUE_ROOT_CAUSES_TOGGLE_ROOT_CAUSE_IN_USE_MODAL
} from '../actions';
import lodashCloneDeep from 'lodash/cloneDeep';

const definitionsInitialState = Immutable.from({
  loading: true,
  error: false,
  isCreating: false,
  isDeleting: false,
  entitiesMap: {},
  issueRootCauseCategoriesIds: [],
  currentTitleEditedRootCauseId: null,
  fetched: false,
  showRootCauseInUseModal: false,
  showRootCauseCategoryInUseModal: false
});

/**
 * Returns an object that contains root cause categories ids and root causes ids on the same level
 * @param entitiesMap
 */
export const flattenIssueRootCauses = entitiesMap =>
  entitiesMap.reduce((acc, category) => {
    const issueRootCauseCategory = lodashCloneDeep(category);
    acc[issueRootCauseCategory.id] = issueRootCauseCategory;
    if (issueRootCauseCategory.rootCauses) {
      issueRootCauseCategory.childrenIds = issueRootCauseCategory.rootCauses.map(rootCause => {
        acc[rootCause.id] = rootCause;
        acc[rootCause.id].parentId = issueRootCauseCategory.id;
        return rootCause.id;
      });
    } else {
      issueRootCauseCategory.childrenIds = [];
    }
    delete issueRootCauseCategory.rootCauses;
    return acc;
  }, {});

export const getIds = entitiesMap => entitiesMap.map(issueRootCauseCategory => issueRootCauseCategory.id);

export default (state = definitionsInitialState, action) => {
  switch (action.type) {
    case ISSUE_ROOT_CAUSES_FETCH_API:
      return state.merge({loading: true, error: false, fetched: false});
    case ISSUE_ROOT_CAUSES_FETCH_API_FULFILLED: {
      return state.merge({
        loading: false,
        error: false,
        fetched: true,
        issueRootCauseCategoriesIds: getIds(action.payload),
        entitiesMap: flattenIssueRootCauses(action.payload)
      });
    }
    case ISSUE_ROOT_CAUSES_FETCH_API_REJECTED:
      return state.merge({error: true, loading: false});
    case ISSUE_ROOT_CAUSE_CATEGORIES_CREATE_START:
      return state.merge({isCreating: true});
    case ISSUE_ROOT_CAUSE_CATEGORIES_CREATE_API_FULFILLED: {
      return state.merge({
        entitiesMap: state.entitiesMap.merge(flattenIssueRootCauses([action.payload])),
        issueRootCauseCategoriesIds: state.issueRootCauseCategoriesIds.concat(action.payload.id),
        isCreating: false
      });
    }
    case ISSUE_ROOT_CAUSE_CATEGORIES_CREATE_API_REJECTED:
      return state.merge({isCreating: false});
    case ISSUE_ROOT_CAUSES_CREATE_START: {
      const parentId = action.payload.parentId;
      const rootCauseId = action.payload.clientID;
      const newChildrenIds = state.entitiesMap[parentId].childrenIds.concat(rootCauseId);
      const newState = state.setIn(['entitiesMap', parentId, 'childrenIds'], newChildrenIds);
      const newRootCause = {...action.payload, id: rootCauseId, parentId};
      return newState.setIn(['entitiesMap', rootCauseId], newRootCause);
    }
    case ISSUE_ROOT_CAUSES_CREATE_API_FULFILLED: {
      const parentId = action.meta && action.meta.parentId;
      const tempId = action.meta && action.meta.clientID;
      let newState = state.setIn(
        ['entitiesMap', parentId, 'childrenIds'],
        state.entitiesMap[parentId].childrenIds.filter(id => id !== tempId).concat(action.payload.id)
      );
      const newRootCause = {...action.payload, parentId};
      newState = newState.merge({entitiesMap: newState.entitiesMap.without(tempId)});
      return newState.setIn(['entitiesMap', [newRootCause.id]], newRootCause);
    }
    case ISSUE_ROOT_CAUSES_CREATE_API_REJECTED: {
      const parentId = action.meta && action.meta.parentId;
      const tempId = action.meta && action.meta.clientID;
      const newState = state.setIn(
        ['entitiesMap', parentId, 'childrenIds'],
        state.entitiesMap[parentId].childrenIds.filter(id => id !== tempId)
      );
      return newState.merge({entitiesMap: newState.entitiesMap.without(tempId)});
    }
    case ISSUE_ROOT_CAUSES_UPDATE_TITLE_START: {
      const rootCauseId = action.payload.id;
      const newTitle = action.payload.title;
      return state.setIn(['entitiesMap', rootCauseId, 'title'], newTitle);
    }
    case ISSUE_ROOT_CAUSES_UPDATE_TITLE_API_FULFILLED: {
      const issueRootCause = state.entitiesMap[action.payload.id];
      if (issueRootCause.parentId) {
        return state.setIn(
          ['entitiesMap'],
          state.entitiesMap.merge({
            [action.payload.id]: {...action.payload, parentId: issueRootCause.parentId}
          })
        );
      }
      return state.setIn(['entitiesMap'], state.entitiesMap.merge(flattenIssueRootCauses([action.payload])));
    }
    case ISSUE_ROOT_CAUSES_UPDATE_STATUS_START: {
      const rootCauseId = action.payload.id;
      const newStatus = action.payload.isActive;
      return state.setIn(['entitiesMap', rootCauseId, 'isActive'], newStatus);
    }
    case ISSUE_ROOT_CAUSES_UPDATE_STATUS_API_FULFILLED: {
      const parentId = action.meta && action.meta.parentId;
      const issueRootCauseId = action.payload.id;
      const newActiveStatus = action.meta.isActive;
      if (parentId) {
        const parent = state.entitiesMap[parentId];
        if (parent.isActive === false && newActiveStatus === true) {
          const updatedEntities = {};
          const rootCausesToUpdate = [parentId, issueRootCauseId];
          rootCausesToUpdate.forEach(id => {
            updatedEntities[id] = {...state.entitiesMap[id], isActive: newActiveStatus};
          });
          return state.setIn(['entitiesMap'], state.entitiesMap.merge(updatedEntities));
        }
        return state.setIn(['entitiesMap', issueRootCauseId, 'isActive'], newActiveStatus);
      }
      return state.setIn(['entitiesMap'], state.entitiesMap.merge(flattenIssueRootCauses([action.payload])));
    }
    case ISSUE_ROOT_CAUSE_CATEGORIES_DELETE_START:
    case ISSUE_ROOT_CAUSES_DELETE_START:
      return state.merge({isDeleting: true});
    case ISSUE_ROOT_CAUSE_CATEGORIES_DELETE_API_REJECTED:
    case ISSUE_ROOT_CAUSES_DELETE_API_REJECTED:
      return state.merge({isDeleting: false});
    case ISSUE_ROOT_CAUSE_CATEGORIES_DELETE_API_FULFILLED:
      return state.merge({
        issueRootCauseCategoriesIds: state.issueRootCauseCategoriesIds.filter(id => id !== action.meta.id),
        entitiesMap: state.entitiesMap.without(action.meta.id),
        isDeleting: false
      });
    case ISSUE_ROOT_CAUSES_DELETE_API_FULFILLED: {
      const parentId = action.meta.parentId;
      const rootCauseId = action.meta.id;
      state = Immutable.setIn(
        state,
        ['entitiesMap', parentId, 'childrenIds'],
        state.entitiesMap[parentId].childrenIds.filter(id => id !== rootCauseId)
      );
      state = Immutable.setIn(state, ['entitiesMap'], state.entitiesMap.without(rootCauseId));
      return state.merge({
        isDeleting: false
      });
    }
    case ISSUE_ROOT_CAUSES_TOGGLE_TITLE_EDITED_ROOT_CAUSE:
      return state.merge({currentTitleEditedRootCauseId: action.payload.id});
    case ISSUE_ROOT_CAUSES_TOGGLE_ROOT_CAUSE_CATEGORY_IN_USE_MODAL:
      return state.merge({showRootCauseCategoryInUseModal: action.payload.isOpen});
    case ISSUE_ROOT_CAUSES_TOGGLE_ROOT_CAUSE_IN_USE_MODAL:
      return state.merge({showRootCauseInUseModal: action.payload.isOpen});
    default:
      return state;
  }
};
